// components/DuePaymentTracker.jsx
import { useState, useEffect, useCallback } from "react";
import { libraryDueSummary } from "../../../api/Library";
import { FeeDepositModal } from "./LibraryComponents";

export default function DuePaymentTracker({ libraryId, onRefresh }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // 'all' | 'overdue'
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (libraryId) params.set("library_id", libraryId);
    if (search) params.set("search", search);
    if (filter === "overdue") params.set("due_status", "overdue");

    libraryDueSummary(params.toString())
      .then((r) => {
        // axios wraps response: r.data.status, r.data.data, r.data.summary
        if (r?.data?.status) {
          setRows(r.data.data ?? []);
          setSummary(r.data.summary ?? null);
        }
      })
      .finally(() => setLoading(false));
  }, [libraryId, search, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");
  const monthLabel = (m) => {
    if (!m) return "";
    const [y, mo] = m.split("-");
    const names = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${names[+mo - 1]} ${y}`;
  };

  return (
    <div>
      {/* ── Summary tiles ──────────────────────────────────── */}
      {summary && (
        <div className="row mb-3">
          <div className="col-sm-4 mb-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body d-flex align-items-center gap-3">
                <div
                  className="avatar bg-label-danger rounded d-flex align-items-center justify-content-center"
                  style={{ width: 50, height: 50, minWidth: 50 }}
                >
                  <i className="ri-user-follow-line fs-4"></i>
                </div>
                <div>
                  <small className="text-muted">Students with Dues</small>
                  <h4 className="mb-0">{summary.total_students_due}</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="col-sm-4 mb-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body d-flex align-items-center gap-3">
                <div
                  className="avatar bg-label-warning rounded d-flex align-items-center justify-content-center"
                  style={{ width: 50, height: 50, minWidth: 50 }}
                >
                  <i className="ri-money-rupee-circle-line fs-4"></i>
                </div>
                <div>
                  <small className="text-muted">Total Outstanding</small>
                  <h4 className="mb-0">{inr(summary.total_outstanding)}</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="col-sm-4 mb-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body d-flex align-items-center gap-3">
                <div
                  className="avatar bg-label-danger rounded d-flex align-items-center justify-content-center"
                  style={{ width: 50, height: 50, minWidth: 50 }}
                >
                  <i className="ri-calendar-close-line fs-4"></i>
                </div>
                <div>
                  <small className="text-muted">Overdue Month Slots</small>
                  <h4 className="mb-0">{summary.total_overdue_months}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Filters ────────────────────────────────────────── */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-2">
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <input
              className="form-control"
              style={{ maxWidth: 280 }}
              placeholder="Search student name or mobile…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="btn-group">
              <button
                className={`btn btn-sm ${filter === "all" ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setFilter("all")}
              >
                All Dues
              </button>
              <button
                className={`btn btn-sm ${filter === "overdue" ? "btn-danger" : "btn-outline-secondary"}`}
                onClick={() => setFilter("overdue")}
              >
                Overdue Only
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────── */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : rows.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <i className="ri-emotion-happy-line fs-1 text-muted"></i>
            <h6 className="mt-2">No outstanding dues</h6>
            <p className="text-muted mb-0">All students are up to date.</p>
          </div>
        </div>
      ) : (
        rows.map((row) => (
          <div
            key={row.library_student_id}
            className="card border-0 shadow-sm mb-2"
          >
            {/* ── Student header ──────────────────────────── */}
            <div className="card-body py-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                {/* Left */}
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center fw-semibold"
                    style={{
                      width: 40,
                      height: 40,
                      minWidth: 40,
                      fontSize: 13,
                    }}
                  >
                    {row.student_name
                      ?.split(" ")
                      .map((x) => x[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                  <div>
                    <div className="fw-semibold">{row.student_name}</div>
                    <small className="text-muted">
                      {row.mobile}
                      {row.seat_no && <> · Seat {row.seat_no}</>}
                      {row.shift_name && <> · {row.shift_name}</>}
                    </small>
                  </div>
                </div>

                {/* Right */}
                <div className="d-flex align-items-center flex-wrap gap-2">
                  {row.overdue_months > 0 && (
                    <span className="badge bg-label-danger">
                      {row.overdue_months} overdue
                    </span>
                  )}
                  <span className="badge bg-label-warning">
                    {row.months_due} month{row.months_due > 1 ? "s" : ""}{" "}
                    pending
                  </span>
                  <strong
                    className="text-danger"
                    style={{ minWidth: 80, textAlign: "right" }}
                  >
                    {inr(row.total_due)}
                  </strong>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => toggle(row.library_student_id)}
                  >
                    <i
                      className={`ri-arrow-${expanded[row.library_student_id] ? "up" : "down"}-s-line`}
                    ></i>{" "}
                    Details
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => setSelected(row)}
                  >
                    <i className="ri-receipt-line me-1"></i> Collect Fee
                  </button>
                </div>
              </div>

              {/* ── Month breakdown ─────────────────────────── */}
              {expanded[row.library_student_id] && (
                <div className="mt-3 border-top pt-3">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Month</th>
                        <th className="text-end">Fee</th>
                        <th className="text-end">Paid</th>
                        <th className="text-end">Due</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {row.month_details.map((md, i) => (
                        <tr key={i}>
                          <td>{monthLabel(md.month)}</td>
                          <td className="text-end">{inr(md.final_amount)}</td>
                          <td className="text-end text-success">
                            {inr(md.paid_amount)}
                          </td>
                          <td className="text-end text-danger">
                            {inr(md.left_amount)}
                          </td>
                          <td>
                            <span
                              className={`badge ${md.is_overdue ? "bg-label-danger" : "bg-label-warning"}`}
                            >
                              {md.is_overdue ? "Overdue" : "Pending"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ))
      )}

      {/* ── Fee deposit modal ──────────────────────────────── */}
      {selected && (
        <FeeDepositModal
          student={selected}
          libraryId={libraryId}
          onClose={() => setSelected(null)}
          onSuccess={() => {
            setSelected(null);
            load();
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
}
